/**
 * `creation_log` → **Settings** (spec M1-B5 §6.2, M2 tier-1 §5.11, card P-123).
 *
 * The captured `Settings` submenu (`editors/creation_log/settings-menu.json`) has no *Customize*
 * row, and the type is read-only — *"system-generated … you cannot update or clear its value"*
 * `[09§A.7]` — so the two keys `creationLogSettingsSchema` declares are the whole dialog:
 *
 *  - `mode` ∈ `personAndDate` | `personOnly` | `dateOnly` — what the cell shows;
 *  - `dateFormatMode` ∈ `dateFormat` | `dateAgo` — the absolute stamp or *3 days ago*.
 *
 * Neither changes the *value*: `toText` stays `"YYYY-MM-DD HH:MM:SS UTC"` for the API whatever
 * the cell paints, because that is the string monday's own `text` carries `[cvs§2 creation_log]`.
 */
import type { CreationLogSettings } from '@elizmonday/shared';
import { HideSummaryField, SegmentedField, SettingsForm, useSettingsDraft } from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const MODES = [
  { id: 'personAndDate', label: 'Person and date', value: 'personAndDate' as const },
  { id: 'personOnly', label: 'Person', value: 'personOnly' as const },
  { id: 'dateOnly', label: 'Date', value: 'dateOnly' as const },
];

const DATE_FORMATS = [
  { id: 'dateFormat', label: 'Date', value: 'dateFormat' as const },
  { id: 'dateAgo', label: 'Time ago', value: 'dateAgo' as const },
];

export function CreationLogSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<CreationLogSettings>) {
  const [draft, patch] = useSettingsDraft<CreationLogSettings>(settings);
  return (
    <SettingsForm
      title="Creation Log column settings"
      testId="creation-log-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Show"
        testId="creation-log-mode"
        value={draft.mode ?? 'personAndDate'}
        options={MODES}
        onChange={(next) => patch({ mode: next })}
      />
      <SegmentedField
        label="Date format"
        testId="creation-log-date-format"
        value={draft.dateFormatMode ?? 'dateFormat'}
        options={DATE_FORMATS}
        onChange={(next) => patch({ dateFormatMode: next })}
      />
      <HideSummaryField
        testId="creation-log-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
